/**
 * ReassignApproverModal — hand a review to another HR Manager (wireframe reassignModal; Ownership design, part 5). Lists
 * the people the server allows (active HR Managers with the approver right and the JD in their view scope); the owner,
 * the delegate and the current approver are shown disabled with the reason. The review keeps its comments and status.
 *   open, row (the JD in review), onCancel(), onReassigned(name)
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { SbButton, SbModal, SbNotification, SbSelect, SbSpin } from 'uiCore';
import { getReassignCandidates, reassignApprover } from 'Api/apiService';
import {
  NEW_APPROVER_LABEL, NEW_APPROVER_PLACEHOLDER, REASSIGN_FAILED_TITLE, REASSIGN_LINK, REASSIGN_TITLE, getAdminError,
  getOwnerOptions, getReassignNote, getReassignedToast, getStatusLabel, ownerName,
} from 'Utils/ownershipTransfer';
import { TransferFieldLabel, TransferHelperText } from 'Pages/PeoplePlusAIV2/dashboard/ManageGroupsModal.styles';
import {
  ConflictProjectItemStyled, ConflictProjectListStyled,
} from 'Components/EmployeeFunctionAccess/EmployeeFunctionAccess.styles';

const LOAD_FAILED = "The list of approvers couldn't be loaded. Close this and try again.";

const ReassignApproverModal = ({ open, row = null, onCancel, onReassigned }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState(null);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !row?.job_id) return undefined;
    let cancelled = false;
    setSelected(null);
    setFailure(null);
    setOptions([]);
    setLoading(true);
    getReassignCandidates(row.job_id)
      .then((data) => { if (!cancelled) setOptions(getOwnerOptions(data)); })
      .catch((err) => { if (!cancelled) setFailure(getAdminError(err, LOAD_FAILED)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, row?.job_id]);

  const pickedName = (options.find((o) => o.value === selected)?.label || selected || '').replace(/ \(.*\)$/, '');

  const handleReassign = async () => {
    setSaving(true);
    try {
      await reassignApprover(row.job_id, selected);
      SbNotification.success(getReassignedToast(row.sb_requisition_number || row.title, pickedName));
      onReassigned(pickedName);
    } catch (err) {
      SbNotification.error(REASSIGN_FAILED_TITLE, getAdminError(err, 'The approver could not be changed. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SbModal
      open={open}
      title={REASSIGN_TITLE}
      onCancel={saving ? undefined : onCancel}
      maskClosable={false}
      destroyOnClose
      width={560}
      footer={[
        <SbButton key="cancel" buttonType="secondary" disabled={saving} onClick={onCancel}>Cancel</SbButton>,
        <SbButton key="ok" buttonType="primary" loading={saving} disabled={!selected || saving} onClick={handleReassign}>
          {REASSIGN_LINK}
        </SbButton>,
      ]}
    >
      {row && (
        <ConflictProjectListStyled>
          <ConflictProjectItemStyled>
            {[row.sb_requisition_number, row.title].filter(Boolean).join(' – ')}{' '}
            <span>
              ({getStatusLabel(row.status)}; owner: {ownerName(row)}; approver: {row.approver?.name || row.approver?.email})
            </span>
          </ConflictProjectItemStyled>
        </ConflictProjectListStyled>
      )}
      <TransferFieldLabel>{NEW_APPROVER_LABEL}</TransferFieldLabel>
      {loading ? <SbSpin size="small" /> : (
        <SbSelect
          options={options}
          value={selected || undefined}
          onChange={(value) => setSelected(value || null)}
          placeholder={NEW_APPROVER_PLACEHOLDER}
          showSearch
          optionFilterProp="label"
          allowClear
          disabled={saving}
          style={{ width: '100%' }}
          getPopupContainer={(trigger) => trigger.parentNode}
        />
      )}
      {failure && <TransferHelperText data-testid="reassign-failure">{failure}</TransferHelperText>}
      <TransferHelperText>{getReassignNote(row?.approver?.name)}</TransferHelperText>
    </SbModal>
  );
};

ReassignApproverModal.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.shape({
    job_id: PropTypes.string,
    sb_requisition_number: PropTypes.string,
    title: PropTypes.string,
    status: PropTypes.string,
    approver: PropTypes.shape({ email: PropTypes.string, name: PropTypes.string }),
  }),
  onCancel: PropTypes.func.isRequired,
  onReassigned: PropTypes.func.isRequired,
};

export default ReassignApproverModal;
